"use client";

import React from "react";
import { Tag as AntTag, ConfigProvider } from "antd";
import type { TagProps as AntTagProps } from "antd";
import { orionTheme } from "../theme/orion-theme";
import clsx from "clsx";

export type TagVariant = "success" | "warning" | "error" | "info" | "default";

export interface TagProps extends AntTagProps {
  /**
   * The variant/type of tag for status pill styling
   * When provided, applies Figma status pill styling
   */
  variant?: TagVariant;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Tag component - Ant Design Tag with OpenStore Orion theming
 *
 * This component provides FULL access to all Ant Design Tag props while
 * automatically applying the OpenStore Orion theme and optional status pill styling.
 *
 * Features:
 * - Full Ant Design Tag API compatibility
 * - Optional Figma status pill styling (when variant is provided)
 * - Automatic theming
 * - Tailwind class override support
 * - ALL Ant Design Tag features and props
 *
 * @example
 * ```tsx
 * // Regular Ant Design Tag (uses Ant Design's built-in colors)
 * <Tag color="blue">Blue Tag</Tag>
 * <Tag color="green">Green Tag</Tag>
 * <Tag color="red">Red Tag</Tag>
 * <Tag closable onClose={handleClose}>Closable Tag</Tag>
 *
 * // Status pill variants (applies Figma styling)
 * <Tag variant="success">Active</Tag>
 * <Tag variant="warning">Draft</Tag>
 * <Tag variant="error">Delete</Tag>
 * <Tag variant="info">Info</Tag>
 * <Tag variant="default">Default</Tag>
 *
 * // Mix Ant Design features with status pill styling
 * <Tag variant="success" closable onClose={handleClose}>
 *   Closable Active
 * </Tag>
 * ```
 */
export const Tag: React.FC<TagProps> = ({
  variant,
  className,
  children,
  color,
  style,
  ...props
}) => {
  // Map variants to Ant Design's built-in colors for better integration
  const getVariantColor = (variant?: TagVariant) => {
    switch (variant) {
      case "success":
        return "green";
      case "warning":
        return "orange";
      case "error":
        return "red";
      case "info":
        return "blue";
      case "default":
        return "default";
      default:
        return color; // Use provided color or Ant Design's default
    }
  };

  // Only apply status pill styling when variant is provided
  const isStatusPill = Boolean(variant);

  return (
    <ConfigProvider theme={orionTheme}>
      <AntTag
        className={clsx(
          "orion-tag",
          variant && `orion-tag--${variant}`,
          isStatusPill && "orion-tag--status-pill",
          className
        )}
        color={getVariantColor(variant)}
        style={{
          ...(isStatusPill && {
            borderRadius: "20px",
            padding: "4px 12px",
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: "1.67",
            fontFamily:
              'Inter, -apple-system, BlinkMacSystemFont, "San Francisco", "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            whiteSpace: "nowrap",
            minHeight: "24px",
            border: "none",
          }),
          ...style,
        }}
        {...props}
      >
        {children}
      </AntTag>
    </ConfigProvider>
  );
};

Tag.displayName = "Tag";

// Export convenience components for common patterns
export const SuccessTag: React.FC<Omit<TagProps, "variant">> = (props) => (
  <Tag variant="success" {...props} />
);

export const WarningTag: React.FC<Omit<TagProps, "variant">> = (props) => (
  <Tag variant="warning" {...props} />
);

export const ErrorTag: React.FC<Omit<TagProps, "variant">> = (props) => (
  <Tag variant="error" {...props} />
);

export const InfoTag: React.FC<Omit<TagProps, "variant">> = (props) => (
  <Tag variant="info" {...props} />
);

// Predefined status tags matching Figma examples
export const ActiveTag: React.FC<Omit<TagProps, "variant" | "children">> = (
  props
) => (
  <Tag variant="success" {...props}>
    Active
  </Tag>
);

export const DraftTag: React.FC<Omit<TagProps, "variant" | "children">> = (
  props
) => (
  <Tag variant="warning" {...props}>
    Draft
  </Tag>
);

export const DeleteTag: React.FC<Omit<TagProps, "variant" | "children">> = (
  props
) => (
  <Tag variant="error" {...props}>
    Delete
  </Tag>
);
